import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import {
	ProductComplianceLabelTypeEnum,
	ProductComplianceTechnicalHeadingTypeEnum
} from '@inter-ikea-kompis/enums';
import type { IProduct, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Text } from '@inter-ikea-kompis/component-text';

import { informationCircle, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';

import type ILawLabelTranslations from '../../types/ILawLabelTranslations.js';
import LawLabelUtility from '../../utilities/LawLabelUtility.js';
import LawLabelCardStyle from './LawLabelCardStyle.js';

const LABEL_FILE_TYPE = 'svg';

/**
 * Law label images and disclaimer text.
 */
export default class LawLabelCard extends KompisElement {
	// A product.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// The translations retrieved from translations service.
	@property({ type: Object, required: true })
	public translations!: ILawLabelTranslations;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	/**
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${LawLabelCardStyle.getStyle({
					theme: this.theme
				})}
			</style>
			<div class="container">${this.getCard()}</div>
		`;
	}

	public override connectedCallback(): void {
		registerIcon(informationCircle);

		this.customElements.define('kompis-text', Text);
		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('skapa-icon', SkapaIcon);

		super.connectedCallback();
	}

	/**
	 * Returns labels and disclaimer text if more than one label is found.
	 */
	private getCard(): RenderResult {
		const labels = this.getLabels();

		return html`
			${labels.length > 1 ? this.getDisclaimerText() : null}
			<div class="labels">${labels}</div>
		`;
	}

	/**
	 * Returns disclaimer text.
	 */
	private getDisclaimerText(): RenderResult {
		const text = this.product.content?.complianceTechnical?.find(
			(label) => label.headingType === ProductComplianceTechnicalHeadingTypeEnum.disclaimer
		)?.text;

		if (!text) {
			return null;
		}

		return html`
			<skapa-inline-message subtle class="message">
				<skapa-icon
					slot="message-icon"
					role="img"
					icon="information-circle"
					aria-hidden="false"
					aria-label="${this.translations.inlineMessageInformativeAriaLabel}"
				></skapa-icon>
				<span slot="body"> ${text} </span>
			</skapa-inline-message>
		`;
	}

	/**
	 * Returns Array of label images.
	 */
	private getLabels(): RenderResult {
		return this.getLabelUrls().map(
			(url) => html`<img src="${url}" alt="${this.getImageAlt(url)}" />`
		);
	}

	/**
	 * Returns url to the label SVGs.
	 */
	private getLabelUrls(): string[] {
		if (this.product.content?.complianceLabel?.length) {
			return this.product.content.complianceLabel
				.filter(
					(label) =>
						label.labelType === ProductComplianceLabelTypeEnum.lawLabel &&
						label.fileType === LABEL_FILE_TYPE
				)
				.map((label) => label.url);
		}
		return [];
	}

	/**
	 * Returns corresponding alt text for the law label image.
	 */
	private getImageAlt(url): string {
		const labelText = LawLabelUtility.getLawLabelLinkText(this.product);

		const labelName = url.split('/').pop().split('.')[0];
		return `${labelText} ${labelName}`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-law-label-card': LawLabelCard;
		'skapa-inline-message': SkapaInlineMessage;
		'skapa-icon': SkapaIcon;
	}
}
